import type { RefObject } from 'react';
import { gsap, ScrollTrigger, useGSAP, MOTION_OK, REDUCED } from '../../lib/gsap';
import { syncThemeColor, useTheme } from '../../lib/useTheme';
import type { Theme } from '../../lib/useTheme';

/** Page canvas of the light theme (Mineral White) and of the dusk chapter / dark theme (Forest Night). */
export const DAY = '#F7F7F0';
export const NIGHT = '#0F1D16';

/**
 * Golden-hour stops between the page canvas and night: the light warms first, then deepens
 * into the forest. A straight blend passes through a flat grey instead.
 * Each stop is [color, share of the scroll window]. The tween starts from the canvas of
 * the current theme: Mineral White in light mode, Forest Night in dark mode (where the
 * warm lift stays dim, so Mineral text keeps AA on every stop).
 */
const SUNSET: Record<Theme, [string, number][]> = {
	light: [
		['#EDE1BA', 0.3],
		['#5E6B4A', 0.3],
		['#22362A', 0.2],
		[NIGHT, 0.2],
	],
	dark: [
		['#323820', 0.3],
		['#2A3825', 0.3],
		['#1A3023', 0.2],
		[NIGHT, 0.2],
	],
};

const BASE: Record<Theme, string> = { light: DAY, dark: NIGHT };

function setChapter(dusk: boolean): void {
	const root = document.documentElement;
	if (dusk) root.dataset.chapter = 'dusk';
	else delete root.dataset.chapter;
	syncThemeColor();
}

/**
 * The page's single theme switch, owned by the Vision section.
 *
 * - --page-bg on <html> is scrubbed from the theme's canvas to dusk through golden-hour stops while
 *   the section travels from "top 85%" to "top 20%". The fiber field morphs into the meadow in the very same
 *   window, so keep both in sync if either changes.
 * - From "top 50%" to the end of the page (Contact and Footer stay dark),
 *   <html data-chapter="dusk"> switches the nav tokens and the browser theme color.
 * - Reduced motion: no scrub, the background snaps at the same "top 50%" point.
 * - The whole thing is rebuilt when the theme changes ('themechange'), so toggling mid-page
 *   restarts the scrub from the new canvas and lands on the right color for the scroll position.
 *
 * Everything is restored when the section unmounts or the motion preference changes.
 */
export function useDuskChapter(section: RefObject<HTMLElement>): void {
	const { theme } = useTheme();

	useGSAP(
		() => {
			const el = section.current;
			if (!el) return;
			const root = document.documentElement;
			const mm = gsap.matchMedia();

			mm.add({ motion: MOTION_OK, reduced: REDUCED }, (context) => {
				const reduced = Boolean(context.conditions?.reduced);

				if (!reduced) {
					const sunset = gsap.timeline({
						scrollTrigger: { trigger: el, start: 'top 85%', end: 'top 20%', scrub: true },
					});
					sunset.set(root, { '--page-bg': BASE[theme] });
					SUNSET[theme].forEach(([color, share]) => {
						sunset.to(root, { '--page-bg': color, ease: 'none', duration: share });
					});
				}

				// end: 'max' reaches progress 1 at the very bottom of the page, so "dusk" is
				// derived from progress > 0 instead of isActive (which turns false there).
				const sync = (self: ScrollTrigger) => {
					const dusk = self.progress > 0;
					setChapter(dusk);
					if (reduced) root.style.setProperty('--page-bg', dusk ? NIGHT : BASE[theme]);
				};

				ScrollTrigger.create({
					trigger: el,
					start: 'top 50%',
					end: 'max',
					onEnter: sync,
					onEnterBack: sync,
					onLeaveBack: sync,
					onRefresh: sync,
				});

				return () => {
					setChapter(false);
					root.style.removeProperty('--page-bg');
				};
			});
		},
		{ scope: section, dependencies: [theme], revertOnUpdate: true },
	);
}
